import { isString, isArray } from 'lodash-es'
import {
  InputLabel,
  FormHelperText,
  type SelectProps as MuiSelectProps,
  type FormControlProps,
  type FormHelperTextProps,
} from '@mui/material'
import type { JSX } from 'react'
import * as $ from './Select.styled'

export interface SelectInputRef<T = unknown> {
  node: HTMLInputElement
  value: T
  focus(): void
}

export type SelectProps<T = unknown> = Omit<MuiSelectProps<T>, 'error' | 'margin' | 'inputRef' | 'disabled'> &
  Pick<FormControlProps, 'margin' | 'sx' | 'required'> & {
    error?: Maybe<string | string[] | boolean>
    helperText?: React.ReactNode
    warning?: boolean
    'data-testid'?: string
    helperTextId?: string
    inputRef?: React.Ref<SelectInputRef<T>>
    helperTextProps?: Omit<FormHelperTextProps, 'children'>
    disabled?: boolean
  }

export const Select = <T,>({
  id,
  helperTextId = id ? `${id}-helper-text` : undefined,
  labelId = id ? `${id}-label` : undefined,
  sx,
  label,
  error,
  margin,
  fullWidth,
  helperText,
  size = 'medium',
  children,
  inputRef,
  warning,
  'data-testid': dataTestId,
  renderValue,
  onChange,
  required,
  disabled,
  helperTextProps,
  SelectDisplayProps,
  ...props
}: SelectProps<T>): JSX.Element => {
  const formHelperText = (() => {
    switch (true) {
      case isString(error):
        return error
      case isArray(error):
        return error.join('; ')
      default:
        return helperText
    }
  })()

  return (
    <$.FormControl
      warning={warning}
      sx={sx}
      size={size}
      margin={margin}
      error={Boolean(error)}
      fullWidth={fullWidth}
      data-testid={dataTestId}
      data-warning={warning}
      disabled={disabled}
      // A read-only select has nothing to pick, so it shouldn't wake up when clicked. MUI derives the
      // floating label, the outline notch and the focus ring from this one flag, so holding it down
      // leaves the label resting inside the field.
      {...(props.readOnly && { focused: false })}
    >
      {label && (
        <InputLabel
          sx={{ color: (theme) => (warning ? theme.tokens.ThemeColorCommonWarning : 'info') }}
          required={required}
          id={labelId}
        >
          {label}
        </InputLabel>
      )}
      <$.Select<T>
        id={id}
        size={size}
        label={label}
        labelId={label ? labelId : undefined}
        data-testid={id}
        inputRef={inputRef}
        SelectDisplayProps={{
          id,
          ...(props.readOnly && { 'aria-readonly': true }),
          ...SelectDisplayProps,
        }}
        onChange={onChange}
        renderValue={renderValue}
        aria-describedby={helperTextId}
        sx={{ ...(warning && { borderColor: ({ tokens }) => tokens.ThemeColorCommonWarning }) }}
        {...props}
      >
        {children}
      </$.Select>
      {formHelperText && (
        <FormHelperText id={helperTextId} {...helperTextProps}>
          {formHelperText}
        </FormHelperText>
      )}
    </$.FormControl>
  )
}
